// Simplified Chinese copy for /web-uploader, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "网页上传工具" and "画廊" as in the site's own zh locale (src/i18n/locales/zh.json); "预览图" and "原图".
// Round figures in 万 (1 万张, 10 万张), as Chinese copy normally writes them.
// The uploader's own labels are in English, so they're quoted as it shows them (“Previews”“Originals”).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const zh: WebUploaderCopy = {
  seo: {
    title: "全新 Photohawk 网页上传工具 | 比 FTP 更快，专为大型活动打造",
    description:
      "我们重新打造了 Photohawk 网页上传工具。它可以同时发送大量照片，轻松处理超过 10 万张照片的上传，连接中断后还会自动续传，所有计划均可使用。在所有计划中，轻量预览图会先上传，让您的画廊在几分钟内上线，而不是几个小时。",
  },
  hero: {
    badge: "新功能",
    eyebrow: "所有计划均可使用",
    title: "全新 Photohawk 网页上传工具",
    subtitle:
      "比 FTP 更快，专为超过 10 万张照片的上传而打造，还能从中断处继续。",
    plans: "在所有计划中，借助预览图优先，大型活动几分钟就能上线，而不是几个小时。",
    ctaPrimary: "打开网页上传工具",
    ctaSecondary: "看看有哪些新功能",
    screenshotAlt:
      "正在上传的网页上传工具。使用预览图优先（所有计划）时，状态栏显示画廊已凭预览图上线以及当前有多少人在访问，右侧队列显示已完成的预览图和正在上传的原图，其中两张已被购买的照片优先上传。",
    phoneAlt: "手机上的网页上传工具，打开的是画廊中的一个文件夹，底部显示上传状态。",
  },
  previews: {
    heading: "预览图优先",
    badge: "所有计划",
    subtitle:
      "在所有计划中：几分钟上线，而不是几个小时。一个 1 万张照片的画廊，在 20 Mbps 的上传速度下，约 8 分钟即可上线并开始销售。",
    body: "轻量预览图会先上传，这样您的画廊可以先上线，开始销售或提供免费下载，原图随后跟上。如果有人在原图到达之前购买或下载了某张照片，这张照片的原图会排到队列最前面。",
    steps: [
      { id: "previews", text: "轻量预览图先上传" },
      { id: "live", text: "您的画廊上线" },
      { id: "buy", text: "有人购买了一张照片" },
      { id: "jump", text: "这张照片的原图排到队列最前面" },
      { id: "originals", text: "其余原图随后跟上" },
    ],
    anim: {
      ...en.previews.anim,
      label: "预览图优先的动画示意",
      description:
        "六张照片的轻量预览图从您的电脑飞入您的画廊，画廊随即切换为“Live”。有人在其中一张照片上点了“Buy”，这张照片的原图便移到原图队列的最前面，最先上传。其余原图随后跟上。",
    },
    link: "预览图优先上传是如何运作的",
  },
  faster: {
    heading: "为什么它比 FTP 更快",
    subtitle: "同样的照片，两种发送方式。",
    anim: {
      ...en.faster.anim,
      label: "FTP 软件与网页上传工具的动画对比",
      description:
        "上下两行。上面一行，FTP 软件默认同时发送 2 张照片，最多不超过 10 张。下面一行，网页上传工具同时发送大量照片，传输通道会随网络状况变宽或变窄。在所有计划中，轻量预览图先上传，所以它的画廊更早上线。连接中断片刻后，上传会自动继续。",
      legendPreviews: "预览图",
      legendOriginals: "原图",
    },
    points: [
      {
        id: "parallel",
        title: "同时发送大量照片",
        body: "它可以同时发送大量照片，远多于 FileZilla（默认同时 2 张，最多不超过 10 张）这样的 FTP 软件。",
      },
      {
        id: "adaptive",
        title: "充分利用您的网络",
        body: "上传过程中它会不断调整，充分利用您的网络。",
      },
      {
        id: "scale",
        title: "为大型活动而打造",
        body: "我们做过大规模测试，超过 10 万张照片的上传它也能轻松应对。",
      },
      {
        id: "live",
        title: "几分钟上线，而不是几个小时（所有计划）",
        body: "在所有计划中，轻量预览图会先上传，一个 1 万张照片的画廊在 20 Mbps 的上传速度下约 8 分钟即可上线并开始销售，原图随后跟上。",
      },
      {
        id: "resume",
        title: "从中断处继续",
        body: "连接中断后，它会自动续传。再次添加同一个文件夹，只会发送缺少的部分。",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "您的上传需要多长时间？",
    subtitle: "根据您自己的上传速度，为您的画廊估算上传时间。",
    heroLink: "估算上传时间",
    photos: "照片数量",
    custom: "其他",
    customLabel: "照片数量（其他）",
    size: "每张原图的平均大小",
    sizeUnit: "MB",
    sizeHint: "活动照片的 JPEG 通常约为 {mb} MB。",
    speed: "您的上传速度",
    speedUnit: "Mbps",
    test: "测试我的上传速度",
    testing: "正在测试… {mbps} Mbps",
    testStarting: "正在测试…",
    testDone: "测得上传速度：{mbps} Mbps",
    testFailed: "暂时无法测量您的速度。请手动输入，或使用通用测速工具。",
    dataNote: "最多消耗约 {mb} MB 流量，并且只在您点击按钮时才会测试。",
    otherTest: "或使用通用测速工具",
    resultsHeading: "估算结果",
    previewsLabel: "预览图优先",
    previewsPlans: "所有计划",
    previewsLive: "画廊上线：{time}",
    previewsAll: "全部原图上传完成：{time}",
    originalsLabel: "仅上传原图",
    originalsLive: "画廊上线：{time}",
    originalsWhen: "即全部 {gb} GB 原图上传完成时",
    computerNote: "在这个速度下，决定整体节奏的是您的电脑生成预览图的速度。",
    disclaimer: "仅为估算。实际时间取决于您的网络、电脑和照片。",
    time: {
      under: "不到 1 分钟",
      min: "约 {n} 分钟",
      h: "约 {n} 小时",
      d: "约 {n} 天",
    },
    table: {
      caption: "示例：1 万张照片",
      speed: "上传速度",
      live: "预览图优先：画廊上线（所有计划）",
      all: "全部原图上传完成",
      note: "约 8 分钟这一数据是在 20 Mbps 上传速度下测得的（预览图优先）。其他各行是同一模型的估算，原图大小按约 {mb} MB 计算。",
    },
  },
  features: {
    heading: "更多新功能",
    subtitle: "新版网页上传工具的真实界面。",
    items: [
      {
        id: "status",
        title: "状态一目了然",
        body: "进度一眼就能看清。使用预览图优先（所有计划）时，先是“Previews”（预览图），然后是“Originals”（原图），各自显示进度。",
        alt: "使用预览图优先（所有计划）时的上传状态：“Live — saving originals…”，Previews 和 Originals 各有一行进度，以及“2 photos people bought go first”。",
      },
      {
        id: "queue",
        title: "队列想放哪里就放哪里",
        body: "可以把上传队列停靠在右侧、左侧或底部，并拖动到您喜欢的大小。浏览器会记住您的选择。",
        alt: "停靠在上传工具底部的上传队列，菜单已打开，可以把它移到右侧、底部或左侧。",
      },
      {
        id: "visitors",
        title: "看看谁在浏览",
        body: "状态栏会显示此刻有多少人正在浏览您的画廊，所有计划均可使用。",
        alt: "上传工具顶部的状态栏：“Live on previews”（使用预览图优先）和访客人数。",
      },
      {
        id: "resume",
        title: "从中断处继续",
        body: "连接中断后，它会自动续传。再次添加同一个文件夹，只会发送缺少的部分。",
        alt: "上传工具中的提示横幅，说明某个文件夹没有上传完，并带有“Resume”按钮。",
      },
      {
        id: "phone",
        title: "在手机上也一样清楚",
        body: "在小屏幕上，文件夹路径会折叠以适应屏幕，点一下“…”就能看到中间的各级文件夹，让您始终清楚照片要传到哪里。",
        alt: "手机上的上传工具：路径显示当前文件夹，点“…”会打开上级文件夹列表。",
      },
    ],
  },
  ftp: {
    date: "2026年11月1日",
    heading: "11月1日起 FTP 的变化",
    body: "我们一直在线的共享 FTP 服务器将于 2026年11月1日关闭。如果您现在在用 FTP，请在此之前完成切换。",
    points: [
      {
        id: "closes",
        title: "共享 FTP 将于 2026年11月1日关闭",
        body: "一直在线的共享 FTP 服务器将于 2026年11月1日停止服务。",
      },
      {
        id: "standard",
        title: "网页上传工具是标准的上传方式",
        body: "所有计划均可使用，今天就能开始用。",
      },
      {
        id: "ondemand",
        title: "如果一定要用 FTP",
        body: "所有计划的工作区成员都可以在网页上传工具中启动自己的按需 FTP 服务器，使用一个永不改变的个人地址。闲置一小时后它会自动关闭。",
      },
    ],
    helpLink: "按需 FTP 是如何运作的",
  },
  more: {
    previewFirstTitle: "预览图优先上传是如何运作的",
    previewFirstBody: "适用于所有计划。一步步了解：预览图、画廊上线，以及买家看到的内容。",
  },
  cta: {
    heading: "下次上传时试试看",
    body: "全新网页上传工具已在所有计划中上线。添加一个文件夹，看着您的画廊上线。",
    button: "打开网页上传工具",
  },
  pause: "暂停动画",
  play: "播放动画",
};

export default zh;
